import React, { useState } from 'react';

// iter-26: shared single-source-of-truth selection card used on every
// "pick one (or more) from a list" page in the wizard.
//
// State table (per spec):
//   Default   — bg #EDEDED, border 1px #BFBFBF
//   Hover     — bg #E5E5E5, border 1px #888
//   Selected  — bg #FFF4ED, border 1.5px #F26C1F, 10×10 dot #F26C1F filled
//   Focus     — current state + 2px outline #F26C1F at 2px offset
//
// Selected branch compensates 0.5px on padding so card heights stay
// identical between default and selected states (border 1px → 1.5px).
//
// Visually-hidden native <input type="radio"|"checkbox"> inside a
// styled <label> wrapper gives keyboard navigation, single/multi-
// select semantics, and announced state for free.
export const ORANGE_PRIMARY = '#F26C1F';
export const ORANGE_TINT = '#FFF4ED';
export const HOVER_BG = '#E5E5E5';
export const HOVER_BORDER = '#888';
export const DEFAULT_BG = '#EDEDED';
export const DEFAULT_BORDER = '#BFBFBF';

export default function SelectionCard({
  id,
  name,
  value,
  label,
  selected,
  disabled,
  multi = false,
  compact = false,
  onSelect,
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const border = selected
    ? `1.5px solid ${ORANGE_PRIMARY}`
    : hovered
      ? `1px solid ${HOVER_BORDER}`
      : `1px solid ${DEFAULT_BORDER}`;
  const bg = selected
    ? ORANGE_TINT
    : hovered
      ? HOVER_BG
      : DEFAULT_BG;
  const basePadY = compact ? 12 : 14;
  const basePadX = compact ? 16 : 18;
  const paddingY = selected ? basePadY - 0.5 : basePadY;
  const paddingX = selected ? basePadX - 0.5 : basePadX;
  const fontSize = compact ? 15 : 16;

  return (
    <label
      htmlFor={id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: '1 1 220px',
        minWidth: 220,
        background: bg,
        border,
        borderRadius: 6,
        padding: `${paddingY}px ${paddingX}px`,
        fontSize,
        fontWeight: selected ? 500 : 400,
        color: '#111',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        outline: focused ? `2px solid ${ORANGE_PRIMARY}` : 'none',
        outlineOffset: focused ? 2 : 0,
        transition: 'background 0.12s ease, border-color 0.12s ease, font-weight 0.12s ease',
        opacity: disabled ? 0.6 : 1,
        userSelect: 'none',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <input
        id={id}
        type={multi ? 'checkbox' : 'radio'}
        name={name}
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      <span>{label}</span>
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: selected ? ORANGE_PRIMARY : 'transparent',
          flexShrink: 0,
        }}
      />
    </label>
  );
}
